import { stageName } from "../utils/scratch-helpers.js";
import { resolveAvatarSrc } from "../../avatars/index.js";
import {
    getActiveTargets,
    getTargetMembers,
    setOnPresenceChange,
} from "./target-presence.js";

const MAX_AVATARS = 2;
const LOOP_INTERVAL_MS = 400;

const badges = new Map(); // targetName -> HTMLElement
let intervalId = null;

const STYLE_ID = "ls-target-presence-style";
const STYLE = `
    .ls-target-presence {
        position: fixed;
        z-index: 15;
        display: flex;
        align-items: center;
        pointer-events: none;
    }
    .ls-target-presence-avatar {
        width: 16px;
        height: 16px;
        border-radius: 50%;
        border: 1.5px solid #fff;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
        margin-left: -6px;
        display: block;
        background: #fff;
    }
    .ls-target-presence-avatar:first-child {
        margin-left: 0;
    }
    .ls-target-presence-more {
        width: 16px;
        height: 16px;
        border-radius: 50%;
        border: 1.5px solid #fff;
        background: #555;
        color: #fff;
        font-size: 0.5rem;
        font-weight: 700;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-left: -6px;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
    }
`;

const ensureStyles = () => {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = STYLE;
    document.head.appendChild(style);
};

const findSpriteItemEl = (targetName) => {
    const nameEl = Array.from(
        document.querySelectorAll(
            '[class*="sprite-selector-item_sprite-name_"]',
        ),
    ).find((el) => el.textContent === targetName);
    return (
        nameEl?.closest(
            '[class*="sprite-selector-item_sprite-selector-item_"]',
        ) || null
    );
};

const findStageEl = () =>
    document.querySelector('[class*="stage-selector_stage-selector_"]');

const buildBadgeContent = (el, members) => {
    const shown = members.slice(0, MAX_AVATARS);
    const extra = members.length - shown.length;

    el.innerHTML = shown
        .map(
            (m) => `
        <img class="ls-target-presence-avatar" style="border-color:${m.color}" src="${resolveAvatarSrc(m.avatar)}" alt="${m.username || ""}" title="${m.username || ""}" />
    `,
        )
        .join("");

    if (extra > 0) {
        el.innerHTML += `<div class="ls-target-presence-more" title="${extra} más">+${extra}</div>`;
    }
};

const getOrCreateBadge = (targetName) => {
    let el = badges.get(targetName);
    if (el) return el;

    ensureStyles();
    el = document.createElement("div");
    el.className = "ls-target-presence";
    document.body.appendChild(el);
    badges.set(targetName, el);
    return el;
};

const destroyBadge = (targetName) => {
    const el = badges.get(targetName);
    if (el) {
        el.remove();
        badges.delete(targetName);
    }
};

const positionOnSpriteItem = (el, itemEl) => {
    const rect = itemEl.getBoundingClientRect();
    el.style.transform = "translate(0, -50%)";
    el.style.left = `${rect.left + 8}px`;
    el.style.top = `${rect.top}px`;
    el.style.display = "flex";
};

const positionOnStage = (el, stageEl) => {
    const rect = stageEl.getBoundingClientRect();
    el.style.transform = "translate(0, -50%)";
    el.style.left = `${rect.left + 8}px`;
    el.style.top = `${rect.top}px`;
    el.style.display = "flex";
};

const renderBadges = () => {
    const activeTargets = getActiveTargets();
    const activeSet = new Set(activeTargets);

    for (const targetName of Array.from(badges.keys())) {
        if (!activeSet.has(targetName)) destroyBadge(targetName);
    }

    for (const targetName of activeTargets) {
        const members = getTargetMembers(targetName);
        if (members.length === 0) {
            destroyBadge(targetName);
            continue;
        }

        const isStage = targetName === stageName;
        const anchorEl = isStage ? findStageEl() : findSpriteItemEl(targetName);

        if (!anchorEl) {
            const el = badges.get(targetName);
            if (el) el.style.display = "none";
            continue;
        }

        const el = getOrCreateBadge(targetName);
        buildBadgeContent(el, members);

        if (isStage) positionOnStage(el, anchorEl);
        else positionOnSpriteItem(el, anchorEl);
    }
};

const stopLoopIfIdle = () => {
    if (getActiveTargets().length === 0 && intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
    }
};

const ensureLoopRunning = () => {
    if (intervalId !== null) return;
    intervalId = setInterval(() => {
        renderBadges();
        stopLoopIfIdle();
    }, LOOP_INTERVAL_MS);
};

const onPresenceChanged = () => {
    renderBadges();
    if (getActiveTargets().length > 0) ensureLoopRunning();
    else stopLoopIfIdle();
};

setOnPresenceChange(onPresenceChanged);
